import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@travellegacy/nexops-design-system';
import { partnerDebtAPI, type PartnerDebtPaymentInput } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Landmark, Loader2, Pencil, Plus, Receipt, Trash2, Wallet } from 'lucide-react';

interface DebtPayment {
  id: string;
  partnerDebtId: string;
  paymentDate: string; // YYYY-MM-DD
  amount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string } | null;
  updatedBy: { id: string; name: string } | null;
}

interface PartnerDebt {
  partnerDebtId: string;
  employeeId: string;
  name: string;
  email: string;
  partnershipPercentage: number;
  calculatedCash: number;
  initialAmount: number;
  totalPaid: number;
  remainingAmount: number;
  payments: DebtPayment[];
}

interface PartnerDebtSummary {
  cutoff: { id: string; month: number; year: number; totalCash: number } | null;
  totals: { initialDebt: number; totalPaid: number; remainingDebt: number };
  partners: PartnerDebt[];
}

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const emptyForm = { partnerDebtId: '', paymentDate: '', notes: '' };

function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function todayIso(): string {
  // en-CA formata como YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

/**
 * Primeiro dia do mês seguinte ao corte (menor data aceita para pagamento)
 */
function firstDayAfterCutoff(month: number, year: number): string {
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
}

const NAME_PARTICLES = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/**
 * Primeiro e segundo nome, pulando partículas ("Caio de Souza Lima" → "Caio Souza")
 */
function shortName(fullName: string): string {
  const [first, ...rest] = fullName.trim().split(/\s+/);
  const second = rest.find((word) => !NAME_PARTICLES.has(word.toLowerCase()));
  return second ? `${first} ${second}` : first ?? '';
}

function toCents(value: number): number {
  return Math.round(value * 100);
}

function formatCurrencyInput(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (!numbers) return '';
  return (parseInt(numbers) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseCurrencyInput(value: string): number {
  if (!value) return 0;
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data;
  return data?.error?.message || fallback;
}

function StatCard({ title, value, icon: Icon, className = '' }: { title: string; value: number; icon: React.ElementType; className?: string }) {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-5 w-5" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <div className={`mt-2 text-3xl font-bold ${className}`}>{formatCurrency(value)}</div>
      </CardContent>
    </Card>
  );
}

export default function PartnerDebtPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [partnerFilter, setPartnerFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<DebtPayment | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [amountDisplay, setAmountDisplay] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [paymentToDelete, setPaymentToDelete] = useState<DebtPayment | null>(null);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['partner-debt'],
    queryFn: async () => {
      const response = await partnerDebtAPI.getSummary();
      return response.data.data as PartnerDebtSummary;
    },
  });

  const partners = summary?.partners ?? [];
  const partnerById = useMemo(() => new Map(partners.map((p) => [p.partnerDebtId, p])), [partners]);

  const payments = useMemo(
    () =>
      partners
        .filter((p) => partnerFilter === 'all' || p.partnerDebtId === partnerFilter)
        .flatMap((p) => p.payments)
        .sort((a, b) => b.paymentDate.localeCompare(a.paymentDate) || b.createdAt.localeCompare(a.createdAt)),
    [partners, partnerFilter]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-debt'] });
    queryClient.invalidateQueries({ queryKey: ['cash-box'] });
  };

  const savePayment = useMutation({
    mutationFn: (data: PartnerDebtPaymentInput) =>
      editingPayment ? partnerDebtAPI.updatePayment(editingPayment.id, data) : partnerDebtAPI.createPayment(data),
    onSuccess: () => {
      invalidate();
      handleCloseModal();
    },
    onError: (error) => setFormError(getApiErrorMessage(error, 'Erro ao salvar pagamento.')),
  });

  const deletePayment = useMutation({
    mutationFn: (id: string) => partnerDebtAPI.deletePayment(id),
    onSuccess: () => {
      invalidate();
      setPaymentToDelete(null);
    },
  });

  // Saldo disponível para o sócio selecionado (na edição, devolve o valor antigo do próprio pagamento)
  const selectedPartner = partnerById.get(formData.partnerDebtId);
  const availableForSelected = selectedPartner
    ? selectedPartner.remainingAmount +
      (editingPayment && editingPayment.partnerDebtId === selectedPartner.partnerDebtId ? editingPayment.amount : 0)
    : 0;

  const handleOpenModal = (payment?: DebtPayment) => {
    setFormError(null);
    if (payment) {
      setEditingPayment(payment);
      setFormData({ partnerDebtId: payment.partnerDebtId, paymentDate: payment.paymentDate, notes: payment.notes ?? '' });
      setAmountDisplay(formatCurrencyInput(String(toCents(payment.amount))));
    } else {
      setEditingPayment(null);
      const defaultPartner =
        partnerFilter !== 'all' ? partnerFilter : partners.find((p) => p.remainingAmount > 0)?.partnerDebtId ?? '';
      setFormData({ ...emptyForm, partnerDebtId: defaultPartner, paymentDate: todayIso() });
      setAmountDisplay('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPayment(null);
    setFormData(emptyForm);
    setAmountDisplay('');
    setFormError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseCurrencyInput(amountDisplay);

    if (!formData.partnerDebtId) {
      setFormError('Selecione o sócio.');
      return;
    }
    if (amount <= 0) {
      setFormError('Informe um valor maior que zero.');
      return;
    }
    if (toCents(amount) > toCents(availableForSelected)) {
      setFormError(`O valor excede o saldo restante (${formatCurrency(Math.max(0, availableForSelected))}).`);
      return;
    }

    setFormError(null);
    savePayment.mutate({
      partnerDebtId: formData.partnerDebtId,
      paymentDate: formData.paymentDate,
      amount,
      notes: formData.notes.trim() || null,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const cutoff = summary?.cutoff;

  if (!summary || !cutoff) {
    return (
      <div className="container space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dívida com os Sócios</h1>
          <p className="text-muted-foreground">Saldo do caixa congelado que a empresa deve aos sócios</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground space-y-2">
            <p>O caixa ainda não foi congelado, então não há dívida registrada.</p>
            <Link to="/financial/cash-box" className="text-primary hover:underline">
              Ir para o Caixa
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { totals } = summary;
  const canRegisterPayment = partners.some((p) => p.remainingAmount > 0);
  const partnerOptions = partners
    .filter((p) => p.initialAmount > 0 || p.partnerDebtId === editingPayment?.partnerDebtId)
    .map((p) => ({
      value: p.partnerDebtId,
      label: `${p.name} (restante ${formatCurrency(p.remainingAmount)})`,
    }));

  return (
    <div className="container space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dívida com os Sócios</h1>
          <p className="text-muted-foreground">
            Saldo do caixa congelado em {MONTH_NAMES[cutoff.month - 1]} {cutoff.year}, pago aos poucos com o caixa da empresa
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenModal()} disabled={!canRegisterPayment}>
            <Plus className="h-4 w-4 mr-2" />
            Registrar pagamento
          </Button>
        )}
      </div>

      {/* Totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Dívida inicial" value={totals.initialDebt} icon={Landmark} className="text-blue-600 dark:text-blue-400" />
        <StatCard title="Total pago" value={totals.totalPaid} icon={Receipt} className="text-green-600 dark:text-green-400" />
        <StatCard title="Saldo devedor" value={totals.remainingDebt} icon={Wallet} className="text-red-600 dark:text-red-400" />
      </div>

      {/* Por sócio */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {partners.map((partner) => {
          const progress = partner.initialAmount > 0 ? Math.min(100, (partner.totalPaid / partner.initialAmount) * 100) : 0;
          return (
            <Card key={partner.partnerDebtId} className={partner.initialAmount === 0 ? 'opacity-70' : undefined}>
              <CardContent className="py-5 space-y-3">
                <div>
                  <p className="font-semibold" title={partner.name}>{shortName(partner.name)}</p>
                  <p className="text-xs text-muted-foreground">{partner.partnershipPercentage}% de participação</p>
                </div>
                {partner.initialAmount === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Sem dívida (caixa calculado no corte: {formatCurrency(partner.calculatedCash)})
                  </p>
                ) : (
                  <>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Saldo inicial</span>
                        <span className="text-blue-600 dark:text-blue-400">{formatCurrency(partner.initialAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pago</span>
                        <span className="text-green-600 dark:text-green-400">{formatCurrency(partner.totalPaid)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Restante</span>
                        <span className="text-red-600 dark:text-red-400">{formatCurrency(partner.remainingAmount)}</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-green-600 dark:bg-green-500" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {progress.toFixed(1)}% quitado · {partner.payments.length} pagamento(s)
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Histórico de pagamentos */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Histórico de pagamentos</CardTitle>
            <div className="w-full sm:w-64">
              <Select
                id="partner-filter"
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Todos os sócios' },
                  ...partners.map((p) => ({ value: p.partnerDebtId, label: p.name })),
                ]}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum pagamento registrado</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Sócio</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead>Registrado por</TableHead>
                  {isAdmin && <TableHead className="w-24">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>{partnerById.get(payment.partnerDebtId)?.name}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell className="text-muted-foreground">{payment.notes || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {payment.createdBy?.name ?? '—'}
                      {payment.updatedBy && (
                        <span className="block text-xs">editado por {payment.updatedBy.name}</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenModal(payment)} aria-label="Editar pagamento">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setPaymentToDelete(payment)} aria-label="Excluir pagamento">
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={isModalOpen} onOpenChange={(open) => (open ? setIsModalOpen(true) : handleCloseModal())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Editar pagamento' : 'Registrar pagamento'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6">
            <Select
              id="partnerDebtId"
              label="Sócio *"
              value={formData.partnerDebtId}
              onChange={(e) => setFormData({ ...formData, partnerDebtId: e.target.value })}
              options={[{ value: '', label: 'Selecione...' }, ...partnerOptions]}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                id="paymentDate"
                label="Data *"
                type="date"
                min={firstDayAfterCutoff(cutoff.month, cutoff.year)}
                max={todayIso()}
                value={formData.paymentDate}
                onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                required
              />
              <div className="space-y-2">
                <label htmlFor="amount" className="text-sm font-medium">
                  Valor *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                  <Input
                    id="amount"
                    type="text"
                    value={amountDisplay}
                    onChange={(e) => setAmountDisplay(formatCurrencyInput(e.target.value))}
                    className="pl-10"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>
            </div>

            {selectedPartner && (
              <p className="text-xs text-muted-foreground">
                Saldo disponível para {selectedPartner.name}: {formatCurrency(Math.max(0, availableForSelected))}
              </p>
            )}

            <Textarea
              id="notes"
              label="Observação"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Opcional"
            />

            {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}

            <DialogFooter>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={savePayment.isPending}>
                  {savePayment.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : editingPayment ? (
                    'Atualizar'
                  ) : (
                    'Registrar'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        open={!!paymentToDelete}
        onOpenChange={(open) => !open && setPaymentToDelete(null)}
        title="Excluir pagamento"
        description={
          paymentToDelete
            ? `Excluir o pagamento de ${formatCurrency(paymentToDelete.amount)} para ${
                partnerById.get(paymentToDelete.partnerDebtId)?.name ?? 'o sócio'
              } em ${formatDate(paymentToDelete.paymentDate)}? O valor volta para o saldo devedor e para o caixa.`
            : ''
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        icon="delete"
        onConfirm={() => paymentToDelete && deletePayment.mutate(paymentToDelete.id)}
        isLoading={deletePayment.isPending}
      />
    </div>
  );
}
